import { useMutation } from '@tanstack/react-query'
import type { RcFile } from 'antd/es/upload'

import {
  attachmentsCreateSignedUrlCreateMutation,
  attachmentsWebhookCreateMutation,
  type CreateUploadUrl,
  type WebhookData
} from 'src/api/main'

/**
 * The attachment endpoints scope every upload to a workspace, and the only way
 * to name it is this field — without it they answer
 * `Organization id: A workspace is required for this upload`, which is what
 * uploading a picture during workspace creation ran into.
 *
 * Not in `CreateUploadUrl`: the DRF view is missing its `@extend_schema`, so the
 * generated body type does not know about a field the backend requires. Drop
 * this cast the moment the schema is annotated and `npm run openapi` is re-run —
 * see AGENTS.md.
 */
type ScopedUploadUrl = CreateUploadUrl & { organization_id?: number }

type UploadResult = {
  id: number
  key: string
  webhook_data: WebhookData
  upload_url: string
}

export const usePresignedUpload = () => {
  const createUrlMutation = useMutation(
    attachmentsCreateSignedUrlCreateMutation()
  )
  const webhookMutation = useMutation(attachmentsWebhookCreateMutation())

  const uploadFile = async (
    file: RcFile,
    /** Workspace the attachment belongs to. Omit only for account-level files. */
    organizationId?: number,
    options?: { isPublic?: boolean }
  ): Promise<UploadResult> => {
    const body: ScopedUploadUrl = {
      title: file?.name,
      type: 'photo',
      is_public: options?.isPublic,
      ...(organizationId === undefined
        ? {}
        : { organization_id: organizationId })
    }

    const final = await createUrlMutation.mutateAsync({ body })

    return {
      id: final?.id,
      key: final?.webhook_data.Key,
      webhook_data: final?.webhook_data,
      upload_url: final?.upload_url
    }
  }

  const submitWebhook = async (webhook_data: WebhookData): Promise<unknown> => {
    const final = await webhookMutation.mutateAsync({ body: webhook_data })

    return final
  }

  return {
    uploadFile,
    submitWebhook,
    isLoading: createUrlMutation.isPending || webhookMutation.isPending
  }
}
